import {
    createContext,
    useContext,
    useState,
    useCallback,
    useLayoutEffect,
    type ReactNode,
} from 'react'
import type { Condominium, CondominiumFormData } from '../utils/types'
import { api } from '../services/API'
import { fetchAllPages } from '../utils/fetchAllPages'
import { aplicarVocabulario, aplicarVocabularioEmpresa, tipoDoSubdominio } from '../i18n/vocabulario'
import { useComposicaoEmpresa } from '../hooks/useComposicaoEmpresa'
import { useAuth } from './AuthContext'


interface CondominiumUpdateData extends CondominiumFormData {
    activo?: Condominium['activo']
}

interface CondominiumContextValue {
    condominiums: Condominium[]
    activeCondominium: Condominium | null
    isLoading: boolean
    fetchCondominiums: () => Promise<void>
    selectCondominium: (id: string) => Promise<void>
    clearActiveCondominium: () => void
    createCondominium: (data: CondominiumFormData) => Promise<Condominium>
    updateCondominium: (id: string, data: CondominiumUpdateData) => Promise<void>
}

const CondominiumContext = createContext<CondominiumContextValue | null>(null)

export function CondominiumProvider({ children }: { children: ReactNode }) {
    const [condominiums, setCondominiums] = useState<Condominium[]>([])
    const [activeCondominium, setActiveCondominium] = useState<Condominium | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    // Os textos seguem o estate activo (propriedade ou condomínio); sem nenhum, os do subdomínio.
    // Layout effect: troca antes de a página se pintar, sem um clarão do vocabulário anterior.
    const tipoActivo = activeCondominium ? activeCondominium.tipo : tipoDoSubdominio()
    useLayoutEffect(() => {
        aplicarVocabulario(tipoActivo)
    }, [tipoActivo])

    // Os ecrãs da empresa toda (Papéis, Definições, Módulos) seguem a carteira: só propriedades ou mista.
    const { user } = useAuth()
    const composicao = useComposicaoEmpresa(!!user)
    useLayoutEffect(() => {
        aplicarVocabularioEmpresa(composicao)
    }, [composicao])

    const fetchCondominiums = useCallback(async () => {
        setIsLoading(true)
        try {
            setCondominiums(await fetchAllPages<Condominium>('/api/condominios'))
            await new Promise((r) => setTimeout(r, 400)) // simula latência
        } finally {
            setIsLoading(false)
        }
    }, [])

    const selectCondominium = useCallback(
        async (id: string) => {
            setIsLoading(true)
            try {
                // Primeiro tenta encontrar na lista já carregada
                const found = condominiums.find((c) => c.id === id)
                if (found) {
                    setActiveCondominium(found)
                    return
                }

                // TODO: se não estiver na lista (ex: refresh da página), buscar na API
                await api.get(`/api/condominios/${id}`)
                    .then((response) => {
                        setActiveCondominium(response.data)
                    }).catch((error) => {
                        throw new Error('Condomínio não encontrado.', error)
                    })

            } finally {
                setIsLoading(false)
            }
        },
        [condominiums]
    )

    const clearActiveCondominium = useCallback(() => {
        setActiveCondominium(null)
    }, [])

    const createCondominium = useCallback(async (data: CondominiumFormData) => {
        const response = await api.post('/api/condominios', data)

        const newItem: Condominium = response.data;

        setCondominiums((prev) => [...prev, newItem])

        return newItem
    }, [])

    const updateCondominium = useCallback(
        async (id: string, data: CondominiumUpdateData) => {
            await api.put(`/api/condominios/${id}`, data)

            setCondominiums((prev) =>
                prev.map((c) => (c.id === id ? { ...c, ...data } : c))
            )

            setActiveCondominium((prev) =>
                prev && prev.id === id ? { ...prev, ...data } : prev
            )
        },
        []
    )

    return (
        <CondominiumContext.Provider
            value={{
                condominiums,
                activeCondominium,
                isLoading,
                fetchCondominiums,
                selectCondominium,
                clearActiveCondominium,
                createCondominium,
                updateCondominium,
            }}
        >
            {children}
        </CondominiumContext.Provider>
    )
}

export function useCondominium() {
    const ctx = useContext(CondominiumContext)
    if (!ctx) {
        throw new Error('useCondominium deve ser usado dentro de um <CondominiumProvider>.')
    }
    return ctx
}
